import {
  Alert,
  Button,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AuthHero } from '@/components/AuthHero';
import {
  makeStateId,
  parseVerifyParams,
  runVerifyFlow,
  saveStoredVerifyData,
  scrubVerifyUrl,
  type VerifyOtpFn,
} from '@/lib/auth/verify-flow';
import { getSupabaseBrowserClient } from '@/lib/supabase/browser';

const EXPIRED_LINK_COPY =
  'This link has expired or is invalid. Please request a new sign-in link to continue.';
const NO_TOKEN_COPY =
  'Unable to verify your link. Please request a new sign-in link to continue.';

export function VerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);
  const [stateKey, setStateKey] = useState<string | null>(null);
  const [next, setNext] = useState<string>('/');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const origin = window.location.origin;
    const parsed = parseVerifyParams(searchParams, origin);
    setNext(parsed.next);

    if (parsed.tokenHash || parsed.token) {
      const st = makeStateId();
      saveStoredVerifyData(st, {
        token_hash: parsed.tokenHash,
        token: parsed.token,
        uid: parsed.uid,
        lt: parsed.lt,
        email: parsed.email,
        type: parsed.type ?? 'magiclink',
        created_at: Date.now(),
      });
      setStateKey(st);

      try {
        window.history.replaceState(
          null,
          '',
          scrubVerifyUrl(window.location.href, parsed.next, st),
        );
      } catch {
        // noop
      }
    } else if (parsed.existingState) {
      setStateKey(parsed.existingState);
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!cancelled && user) {
          navigate(next, { replace: true });
        }
      } catch {
        // ignore
      }
    };
    if (next) void check();
    return () => {
      cancelled = true;
    };
  }, [next, navigate, supabase]);

  const handleVerify = async () => {
    setErrorMsg(null);
    setVerifying(true);
    try {
      const st = stateKey || searchParams.get('state');
      const verifyOtp: VerifyOtpFn = args => supabase.auth.verifyOtp(args);
      const outcome = await runVerifyFlow(st, { verifyOtp });

      switch (outcome.kind) {
        case 'success':
          navigate(next, { replace: true });
          return;
        case 'no_state':
          setErrorMsg(NO_TOKEN_COPY);
          return;
        case 'no_token':
          setErrorMsg(NO_TOKEN_COPY);
          return;
        case 'expired':
          // runVerifyFlow owns sessionStorage cleanup — it deliberately
          // leaves the payload intact when recovery hits a transient
          // failure so a refresh can retry. The view should NOT clear it.
          setErrorMsg(EXPIRED_LINK_COPY);
          return;
        default:
          setErrorMsg(
            outcome.message ?? 'Verification failed. Please try again.',
          );
      }
    } catch (e) {
      setErrorMsg(
        e instanceof Error
          ? e.message
          : 'Verification failed. Please try again.',
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <AuthHero width={460}>
      <Text
        size="md"
        ta="center"
        c="navy.2"
        mb={24}
        style={{ lineHeight: 1.6 }}
      >
        One more step to complete your login
      </Text>

      <Paper withBorder shadow="lg" p={40} radius="lg">
        <Stack gap="lg">
          {errorMsg && (
            <Alert
              color="red"
              title="Verification Issue"
              icon={<IconAlertCircle size={20} />}
              styles={{
                root: { borderRadius: 8 },
              }}
            >
              {errorMsg}
            </Alert>
          )}

          {!errorMsg && (
            <Text ta="center" size="sm" c="navy.7" style={{ lineHeight: 1.6 }}>
              Please click the button below to complete your login and access
              your Discovery Dialogue interview.
            </Text>
          )}

          <Button
            onClick={handleVerify}
            loading={verifying}
            size="lg"
            fullWidth
            styles={{
              root: {
                fontWeight: 600,
                height: 48,
                fontSize: '1rem',
              },
            }}
          >
            {verifying ? 'Verifying...' : 'Continue to Interview'}
          </Button>

          {errorMsg && (
            <Text ta="center" size="xs" c="dimmed" style={{ marginTop: 8 }}>
              Having trouble?{' '}
              <Text
                component="a"
                href={`/auth/login?redirectTo=${encodeURIComponent(next)}`}
                c="navy.7"
                td="underline"
                style={{ cursor: 'pointer' }}
              >
                Request a new sign-in link
              </Text>
            </Text>
          )}
        </Stack>
      </Paper>
    </AuthHero>
  );
}

export function VerifyPageLoadingFallback() {
  return (
    <AuthHero width={460}>
      <Center h={200}>
        <Loader size="lg" color="navy.2" />
      </Center>
    </AuthHero>
  );
}
